/**
 * TypeScript types for Billing Claims
 */

export enum ClaimType {
  MEDICARE = 'MEDICARE',
  MEDICAID = 'MEDICAID',
  PRIVATE_INSURANCE = 'PRIVATE_INSURANCE',
}

export enum ClaimFormType {
  CMS_1500 = 'CMS_1500',
  UB_04 = 'UB_04',
  INVOICE = 'INVOICE',
}

export enum ClaimStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  PAID = 'PAID',
  PARTIAL_PAID = 'PARTIAL_PAID',
}

export enum SubmissionMethod {
  ELECTRONIC = 'ELECTRONIC',
  PAPER = 'PAPER',
  EDI = 'EDI',
}

export interface BillingClaimDTO {
  id?: string;
  claimNumber?: string;
  formType?: ClaimFormType;
  residentId: string;
  residentName?: string;
  claimType: ClaimType;
  payerName: string;
  payerId?: string;
  policyNumber?: string;
  serviceDateFrom: string;
  serviceDateTo: string;
  diagnosisCodes: string;
  procedureCodes: string;
  billedAmount: number;
  allowedAmount?: number;
  paidAmount?: number;
  patientResponsibility?: number;
  adjustmentAmount?: number;
  balance?: number;
  status?: ClaimStatus;
  submissionDate?: string;
  submissionMethod?: SubmissionMethod;
  adjudicationDate?: string;
  paymentDate?: string;
  denialReason?: string;
  denialCode?: string;
  appealFiled?: boolean;
  appealDate?: string;
  appealStatus?: string;
  placeOfService?: string;
  renderingProviderNpi?: string;
  billingProviderNpi?: string;
  createdByUser?: string;
  approvedByUser?: string;
  notes?: string;
  /** Full claim form; only returned when a single claim is fetched. */
  formData?: Record<string, unknown>;
  editable?: boolean;
  tenantId?: string;
  facilityId?: string;
  createdAt?: string;
  updatedAt?: string;
  daysOutstanding?: number;
  hasPatientSignature?: boolean;
  patientSignatureType?: string;
  patientSignatureDate?: string;
  hasInsuredSignature?: boolean;
  insuredSignatureType?: string;
  insuredSignatureDate?: string;
}

export interface ClaimSignatureDTO {
  signatureSlot?: string;
  signatureType?: string;
  signatureDate?: string;
  hasSignature?: boolean;
}

/** Body for creating a claim or replacing a draft. Status, claim number and resident name are set by the server. */
export interface BillingClaimRequest {
  formType: ClaimFormType;
  residentId: string;
  claimType: ClaimType;
  payerName: string;
  payerId?: string;
  policyNumber?: string;
  serviceDateFrom?: string;
  serviceDateTo?: string;
  diagnosisCodes: string;
  procedureCodes: string;
  billedAmount: number;
  allowedAmount?: number;
  paidAmount?: number;
  submissionMethod?: SubmissionMethod;
  placeOfService?: string;
  renderingProviderNpi?: string;
  billingProviderNpi?: string;
  notes?: string;
  formData?: Record<string, unknown>;
}

export interface BillingClaimSearchParams {
  status?: ClaimStatus[];
  formType?: ClaimFormType;
  search?: string;
  residentId?: string;
  serviceDateFrom?: string;
  serviceDateTo?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface BillingClaimSummary {
  totalClaims: number;
  countsByStatus: Record<ClaimStatus, number>;
  totalBilled: number;
  totalPaid: number;
  outstandingBalance: number;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// ── Settlements ────────────────────────────────────────────────────────────

export type PaymentMode = 'CASH' | 'BANK_TRANSFER' | 'CHEQUE' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'INSURANCE' | 'OTHER';

export type SettlementPayerType =
  | 'PRIMARY_INSURANCE'
  | 'SECONDARY_INSURANCE'
  | 'PATIENT'
  | 'GUARANTOR'
  | 'MEDICARE'
  | 'MEDICAID'
  | 'OTHER';

export type SettlementTransactionType = 'PAYMENT' | 'REFUND' | 'ADJUSTMENT';

export type ClaimSettlementStatus = 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | 'OVERPAID';

/** Body of POST /billing-claims/{id}/settlements. Totals are computed by the server. */
export interface RecordSettlementRequest {
  transactionType?: SettlementTransactionType;
  amount: number;
  paymentMode: PaymentMode;
  /** yyyy-MM-dd */
  transactionDate: string;
  payerType: SettlementPayerType;
  payerName?: string;
  paymentReference?: string;
  reasonCode?: string;
  remarks?: string;
}

export interface BillingClaimSettlementDTO {
  id: string;
  receiptNumber: string;
  claimId: string;
  claimNumber: string;
  transactionType: SettlementTransactionType;
  status: 'POSTED' | 'VOIDED';
  source: 'MANUAL' | 'CLAIM_SUBMISSION' | 'MIGRATION';
  amount: number;
  paymentMode: PaymentMode;
  payerType: SettlementPayerType;
  payerName?: string;
  paymentReference?: string;
  transactionDate: string;
  remarks?: string;
  billedAmount: number;
  previouslyPaid: number;
  totalPaidAfter: number;
  adjustmentsAfter: number;
  balanceAfter: number;
  recordedByName?: string;
  recordedAt?: string;
  voidedAt?: string;
  voidReason?: string;
}

export interface ClaimSettlementSummary {
  claimId: string;
  claimNumber: string;
  claimStatus: ClaimStatus;
  residentName: string;
  payerName: string;
  policyNumber?: string;
  totalBilled: number;
  totalPayments: number;
  totalRefunds: number;
  /** Payments minus refunds ("previously paid" for the next settlement). */
  totalPaid: number;
  totalAdjustments: number;
  balanceDue: number;
  settlementStatus: ClaimSettlementStatus;
  postedCount: number;
  voidedCount: number;
  lastPaymentDate?: string;
  /** What the signed-in user may post now; empty when nothing. */
  allowedTransactionTypes: SettlementTransactionType[];
}

export interface InvoiceLine {
  id: string;
  serviceLookupId?: string;
  productOrService: string;
  description: string;
  qty: number | string;
  rate: number | string;
  amount: number;
}

export interface InvoiceFormData {
  providerDba: string;
  providerName: string;
  providerAddress: string;
  providerCity: string;
  providerState: string;
  providerZip: string;
  providerEmail: string;
  providerPhone: string;
  providerWebsite: string;
  providerLicenseNo: string;
  providerNpi: string;
  billToClientName: string;
  billToAddress: string;
  shipToAddress: string;
  invoiceNumber: string;
  terms: string;
  invoiceDate: string;
  dueDate: string;
  residentName: string;
  residentCin: string;
  residentDob: string;
  servicePeriodFrom: string;
  servicePeriodTo: string;
  managedCarePlan: string;
  serviceProvider?: string;
  paymentMethod: string;
  totalAmount: number;
  statusLabel?: string;
  overdueDate?: string;
  notes?: string;
}

export interface IndividualInvoiceItem {
  itemNumber?: number;
  serviceLookupId?: string;
  productOrService: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface IndividualInvoiceRequest {
  residentId: string;
  facilityId?: string;
  residentName?: string;
  residentCin?: string;
  residentDob?: string;
  servicePeriodFrom?: string;
  servicePeriodTo?: string;
  managedCarePlan?: string;
  payerName?: string;
  billTo?: string;
  shipTo?: string;
  paymentTerms?: string;
  invoiceDate?: string;
  dueDate?: string;
  items: IndividualInvoiceItem[];
  paymentMethod?: string;
  status?: string;
  statusLabel?: string;
  notes?: string;
}

export interface IndividualInvoiceDTO extends IndividualInvoiceRequest {
  id?: string;
  invoiceNumber?: string;
  totalAmount?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}
